import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Divider } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import Swal from "sweetalert2";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";

import bksAPI from "services/api/bks";
import { useSoftUIController } from "hooks/context";
import groupAPI from "services/api/groups";
import SoftSelect from "components/SoftComponents/SoftSelect";

const colourOptions = [
    "#6CB0B1", "#539899", "#3C8082", "#27696B",
    "#69ADC3", "#5195AE", "#397E98", "#246882",
    "#76A6CC", "#5B8DB7", "#4175A0", "#2A5E8A",
    "#8596A9", "#6A7E94", "#50667F", "#384F69"
];

function SiteGroupForm({ appMode, selectedSites = [], finalPolygonData = null, onSuccess, onRemoveSite, onCancel,
    selectedColor, setSelectedColor, onGeneratePolygon, onEditMode, editGroup, setEditGroupId,
    groupType, setGroupType, selectedDevices = [], onRemoveDevice }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    bksAPI.loadToken(useAuth0());
    const formRef = useRef(null);
    const queryClient = useQueryClient();

    const usesDevices = groupType === 'monitoring';

    const extractedSiteIds = Array.from(new Set(selectedSites.map((site) => site.properties?.site_id).filter((id) => id != null && id !== "")));
    const extractedDeviceUUIDs = Array.from(new Set(selectedDevices.map((dev) => dev.properties?.uuid).filter((id) => id != null && id !== "")));

    const visibleGroupOptions = userPermissions.filter(prm => prm.includes('group:')).map(prm => {
        var ret = prm.split(':')[1];
        return { value: ret, label: ret.charAt(0).toUpperCase() + ret.slice(1) };
    });

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm({
        defaultValues: { name: "", type: "campaigns", color: "" }
    });

    useEffect(() => {
        if (editGroup) {
            setValue("name", editGroup.name || "");
            setValue("type", editGroup.type || "campaigns");
            setValue("color", editGroup.color || editGroup.properties?.feature_colour || "");
        }
    }, [editGroup, setValue]);

    const triggerFormSubmit = () => {
        formRef.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
        );
    };

    const { mutateAsync, isPending } = useMutation({
        mutationFn: async (values) => {
            if (editGroup) {
                return await groupAPI.updateGroup(editGroup._id || editGroup.group_id, values);
            }
            return await groupAPI.createGroup(values);
        },
        onMutate: () => {
            Swal.fire({
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saving...</div>`,
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
        },
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: ['getAllGroups', selectedOrg] });
            if (editGroup) {
                queryClient.invalidateQueries({ queryKey: ["getGroupWithID", editGroup._id || editGroup.group_id, selectedOrg] });
            }

            Swal.close();

            await Swal.fire({
                icon: 'success',
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Group ${editGroup ? 'Updated' : 'Created'}!</div>`,
                showConfirmButton: false,
                timer: 2000
            });

            if (editGroup && setEditGroupId) setEditGroupId(null);
            if (onSuccess) await onSuccess();
        },
        onError: async (error) => {
            console.error(error);
            Swal.close();
            await Swal.fire({
                icon: "error",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`,
                html: error.message
                    ? `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${error.message}</div>`
                    : `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">There was a problem saving your group.</div>`,
            });
        }
    });

    async function submitForm(formData) {
        if (!formData.name.trim()) {
            return Swal.fire({
                icon: "warning",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Missing Name</div>`,
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Please provide a name for the group.</div>`,
            });
        }

        const payload = {
            ...formData,
            geojson: finalPolygonData ? [finalPolygonData] : [],
            sites: usesDevices ? [] : extractedSiteIds,
            uuids: usesDevices ? extractedDeviceUUIDs : []
        };

        await mutateAsync(payload);
    }

    return (
        <SoftBox
            component="form"
            ref={formRef}
            onSubmit={handleSubmit((data) => { submitForm(data); })}
            style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
            <SoftBox>
                <SoftTypography p={2} sx={{ background: '#f9f9f9' }} variant={deviceLayout === 'mobile' ? "h5" : "h4"}>
                    {editGroup ? "Edit Group" : "Create Group"}
                </SoftTypography>
                <Divider />
                <Grid container spacing={2} px={2} pt={2} pb={1}>
                    <Grid item xxs={12}>
                        <SoftBox pl={1} my={0.5}>
                            <SoftTypography variant="body2" fontWeight='bold'>Group Name</SoftTypography>
                        </SoftBox>
                        <SoftBox>
                            <SoftInput placeholder="Enter Group Name..." {...register('name', { required: true })} error={!!errors.name} />
                        </SoftBox>
                    </Grid>
                    <Grid item xxs={12}>
                        <SoftBox pl={1} my={0.5}>
                            <SoftTypography variant="body2" fontWeight='bold'>Group Type</SoftTypography>
                        </SoftBox>
                        <SoftBox>
                            <Controller
                                name="type"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SoftSelect
                                        bgColor={"white"}
                                        value={{ value, label: (value.charAt(0).toUpperCase() + value.slice(1)) }}
                                        options={visibleGroupOptions}
                                        onChange={(selectedOption) => {
                                            onChange(selectedOption.value);
                                            if (setGroupType) setGroupType(selectedOption.value);
                                        }}
                                    />
                                )}
                            />
                        </SoftBox>
                    </Grid>
                    <Grid item xxs={12}>
                        <SoftBox pl={1} my={0.5}>
                            <SoftTypography variant="body2" fontWeight='bold'>Group Colour</SoftTypography>
                        </SoftBox>
                        <SoftBox>
                            <Controller
                                name="color"
                                control={control}
                                render={({ field: { onChange, value } }) => {
                                    const button = (opt) => (
                                        <SoftButton onClick={() => { setValue("color", opt); setSelectedColor(opt); }}
                                            sx={({ functions: { hexToRgb } }) => ({
                                                flex: 1, padding: 0, minWidth: 0, borderRadius: 0, backgroundColor: opt,
                                                '&:hover': { backgroundColor: `rgba(${hexToRgb(opt)}, 1) !important`, zIndex: 1, transform: 'scale(1.1)' },
                                                '&:focus:not(:hover)': { backgroundColor: `rgba(${hexToRgb(opt)}, 1) !important`, zIndex: 1, transform: 'scale(1.1)', boxShadow: 0 },
                                                transform: selectedColor == opt ? 'scale(1.1)' : 0, zIndex: selectedColor == opt ? 1 : 0
                                            })}
                                        />
                                    );
                                    return (
                                        <SoftBox display="flex" flexDirection="column">
                                            <SoftBox display="flex">{colourOptions.slice(0, colourOptions.length / 2).map(opt => button(opt))}</SoftBox>
                                            <SoftBox display="flex">{colourOptions.slice(colourOptions.length / 2).map(opt => button(opt))}</SoftBox>
                                        </SoftBox>
                                    )
                                }}
                            />
                        </SoftBox>
                    </Grid>
                </Grid>
            </SoftBox>

            <SoftBox sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {usesDevices ? (
                    <SoftBox sx={{ padding: '0 20px 20px 20px', flex: 1 }}>
                        <SoftTypography sx={{ color: '#666', fontSize: '14px', marginTop: 2 }}>
                            {selectedDevices.length} devices selected
                        </SoftTypography>

                        {selectedDevices.length === 0 ? (
                            <SoftTypography sx={{ fontStyle: 'italic', color: '#999', fontSize: '14px', mt: 1 }}>
                                Select devices using the map or grid view.
                            </SoftTypography>
                        ) : (
                            <SoftBox sx={{ listStyle: 'none', padding: 0, margin: 0, mt: 1 }}>
                                {selectedDevices.map((device) => (
                                    <SoftBox key={device.properties.uuid} mb={1.25} p={1} sx={{ background: '#fff', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${selectedColor || '#17c1e8'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <SoftBox>
                                            <SoftTypography fontWeight="bold" sx={{ display: 'block', fontSize: '14px' }}>{device.properties.name}</SoftTypography>
                                            <SoftTypography sx={{ fontSize: '12px', color: '#555' }}>UUID: {device.properties.uuid}</SoftTypography>
                                        </SoftBox>
                                        <SoftBox onClick={() => onRemoveDevice(device.properties.uuid)} sx={({ typography: { fontFamily } }) => ({ fontFamily, padding: '0.5rem !important', color: '#e74c3c', cursor: 'pointer', fontSize: '18px' })}>&times;</SoftBox>
                                    </SoftBox>
                                ))}
                            </SoftBox>
                        )}
                    </SoftBox>
                ) : appMode === 'edit' ? (
                    <SoftBox sx={{ padding: '0 20px 20px 20px', flex: 1 }}>
                        <SoftTypography sx={{ color: '#666', fontSize: '14px', marginTop: 2 }}>
                            {selectedSites.length} sites selected
                        </SoftTypography>

                        {selectedSites.length === 0 ? (
                            <SoftTypography sx={{ fontStyle: 'italic', color: '#999', fontSize: '14px', mt: 1 }}>
                                Draw a polygon on the map or click a site to select.
                            </SoftTypography>
                        ) : (
                            <SoftBox sx={{ listStyle: 'none', padding: 0, margin: 0, mt: 1 }}>
                                {selectedSites.map((site) => (
                                    <SoftBox key={site.properties.site_id} mb={1.25} p={1} sx={{ background: '#fff', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${site.properties.feature_colour}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <SoftBox>
                                            <SoftTypography fontWeight="bold" sx={{ display: 'block', fontSize: '14px' }}>{site.properties.site_name}</SoftTypography>
                                            <SoftTypography sx={{ fontSize: '12px', color: '#555' }}>Site ID: {site.properties.site_id}</SoftTypography>
                                        </SoftBox>
                                        <SoftBox onClick={() => onRemoveSite(site.properties.site_id)} sx={({ typography: { fontFamily } }) => ({ fontFamily, padding: '0.5rem !important', color: '#e74c3c', cursor: 'pointer', fontSize: '18px' })}>&times;</SoftBox>
                                    </SoftBox>
                                ))}
                            </SoftBox>
                        )}
                    </SoftBox>
                ) : (
                    <SoftBox m={1} p={1} sx={{ borderRadius: '8px', backgroundColor: '#eee' }}>
                        <SoftTypography variant="subtitle2" color="dark" sx={{ marginTop: 1, marginLeft: 1 }}>
                            {usesDevices ? selectedDevices.length : selectedSites.length} Items Selected
                        </SoftTypography>
                        <SoftBox p={1}>
                            {(usesDevices ? selectedDevices : selectedSites).map((item, idx) => (
                                <SoftBox key={usesDevices ? item.properties.uuid : item.properties.site_id} mb={idx == (usesDevices ? selectedDevices.length : selectedSites.length) - 1 ? 0 : 1.25} p={1} sx={{ background: '#fff', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${usesDevices ? (selectedColor || '#17c1e8') : item.properties.feature_colour}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <SoftBox>
                                        <SoftTypography fontWeight="bold" sx={{ display: 'block', fontSize: '14px' }}>{usesDevices ? item.properties.name : item.properties.site_name}</SoftTypography>
                                        <SoftTypography sx={{ fontSize: '12px', color: '#555' }}>ID: {usesDevices ? item.properties.uuid : item.properties.site_id}</SoftTypography>
                                    </SoftBox>
                                </SoftBox>
                            ))}
                        </SoftBox>
                    </SoftBox>
                )}
            </SoftBox>

            <SoftBox>
                <Divider />
                <SoftBox sx={{ background: '#f9f9f9' }} display='flex' flexDirection='row' justifyContent='space-between' gap={1} p={2}>
                    {appMode === 'edit' ? (
                        <>
                            <SoftButton variant="outlined" color="error" onClick={onCancel} sx={{ flex: 1 }}>Cancel</SoftButton>
                            <SoftButton variant="gradient" color="dark" disabled={(usesDevices ? selectedDevices.length : selectedSites.length) === 0} onClick={onGeneratePolygon} sx={{ flex: 2 }}>Generate Polygon</SoftButton>
                        </>
                    ) : (
                        <>
                            <SoftButton variant="outlined" color="secondary" onClick={onEditMode} sx={{ flex: 1 }}>Edit</SoftButton>
                            <SoftButton variant="gradient" color="dark" disabled={isPending || (usesDevices ? selectedDevices.length : selectedSites.length) === 0} onClick={() => triggerFormSubmit()} sx={{ flex: 2 }}>{editGroup ? "Update" : "Save"}</SoftButton>
                        </>
                    )}
                </SoftBox>
            </SoftBox>
        </SoftBox>
    );
}

SiteGroupForm.propTypes = {
    appMode: PropTypes.string, selectedSites: PropTypes.array, finalPolygonData: PropTypes.object,
    onRemoveSite: PropTypes.func, onCancel: PropTypes.func, selectedColor: PropTypes.string,
    setSelectedColor: PropTypes.func, onGeneratePolygon: PropTypes.func, onEditMode: PropTypes.func,
    onSuccess: PropTypes.func, editGroup: PropTypes.object, setEditGroupId: PropTypes.func,
    groupType: PropTypes.string, setGroupType: PropTypes.func, selectedDevices: PropTypes.array, onRemoveDevice: PropTypes.func
};

export default SiteGroupForm;